import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { NoddiCircularLoaderWithTrans } from '@components/Loading/NoddiCircularLoaderWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { UserGroupMemberRecordListPartner } from '@navio/nsdk';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, NoddiSwitch } from 'noddi-ui-web';
import { RemoveOrganizationMemberDialog, useAuthContext, type RemovableMember } from 'noddi-web';
import { useState } from 'react';
import { useBrandCapabilities } from '../../../hooks/useBrandCapabilities';
import { useReturnToGroupPicker } from '../../../hooks/useReturnToGroupPicker';
import { getCommonTranslations } from '../../../utils/commonTranslations';

type SmsField = 'smsBookingNotifications' | 'smsRecallProposals';

/**
 * A plain member's own SMS settings and the way out of the organization. The members page that
 * holds both for admins is admin-only, so without this a member could neither silence the SMS nor
 * leave. Reads the member's own row from the member list, which every member may call.
 */
export const OrganizationMemberSettings = ({ userGroupId }: { userGroupId: number }) => {
  const { userData } = useAuthContext();
  const userId = userData?.user.id;
  const texts = getCommonTranslations().notificationGroupOverview;
  const onSelfRemoved = useReturnToGroupPicker();
  const [isLeaving, setIsLeaving] = useState(false);

  const { data, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupMembers,
    input: { userGroupId, userId, pageSize: 1 },
    queryConfig: { enabled: userId != null }
  });
  const membership = data?.results[0];

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>Your membership</Trans>
      </h2>
      {error && <ApiErrorMessageWithTrans error={error} />}
      {!data ? (
        !error && <NoddiCircularLoaderWithTrans />
      ) : !membership ? null : (
        <NoddiBasicCard className='flex flex-col gap-4'>
          <SmsSettings userGroupId={userGroupId} membership={membership} />
          <div className='flex justify-end'>
            <NoddiButton
              variant='destructiveOutline'
              size='small'
              startIcon='LogOut'
              onClick={() => setIsLeaving(true)}
            >
              {texts.leaveGroup}
            </NoddiButton>
          </div>
        </NoddiBasicCard>
      )}
      {membership && (
        <RemoveOrganizationMemberDialog
          userGroupId={userGroupId}
          member={toRemovableMember(membership)}
          // The admin role is what this page is for the absence of.
          isOnlyAdmin={false}
          currentUserId={userId}
          translations={texts}
          showMutationToasts
          open={isLeaving}
          onSelfRemoved={onSelfRemoved}
          onClose={() => setIsLeaving(false)}
        />
      )}
    </section>
  );
};

const toRemovableMember = (membership: UserGroupMemberRecordListPartner): RemovableMember => ({
  userId: membership.user.id,
  userGroupMemberId: membership.id,
  firstName: membership.user.firstName,
  lastName: membership.user.lastName,
  isPrimaryContact: membership.isPrimaryContact
});

const SmsSettings = ({
  userGroupId,
  membership
}: {
  userGroupId: number;
  membership: UserGroupMemberRecordListPartner;
}) => {
  const texts = getCommonTranslations().notificationGroupOverview;
  const {
    mutate: patchMember,
    isPending,
    variables,
    error
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupMember,
    queryConfig: {
      onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId })
    }
  });

  const { showsRecalls } = useBrandCapabilities();

  // Shows the value being saved rather than snapping back until the refetch lands.
  const valueOf = (field: SmsField) => (isPending ? (variables?.[field] ?? membership[field]) : membership[field]);

  const allRows: { field: SmsField; label: string; description: string }[] = [
    {
      field: 'smsBookingNotifications',
      label: texts.bookingSms,
      description: t`Confirmation, arrival time and completion of bookings for your cars.`
    },
    {
      field: 'smsRecallProposals',
      label: texts.serviceReminderSms,
      description: t`A heads-up when one of your cars is due for a seasonal wheel change.`
    }
  ];
  const rows = allRows.filter(({ field }) => showsRecalls || field !== 'smsRecallProposals');

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <p className='font-semibold'>{texts.notifications}</p>
        <p className='text-sm text-muted-foreground'>
          <Trans>Sent only for the cars you drive.</Trans>
        </p>
      </div>
      {rows.map(({ field, label, description }) => (
        <label
          key={field}
          htmlFor={field}
          className='flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-border/60 p-3'
        >
          <span className='flex flex-col gap-1'>
            <span className='font-medium'>{label}</span>
            <span className='text-sm text-muted-foreground'>{description}</span>
          </span>
          <NoddiSwitch
            id={field}
            checked={valueOf(field)}
            disabled={isPending}
            onCheckedChange={(checked) => patchMember({ userGroupMemberId: membership.id, [field]: checked })}
          />
        </label>
      ))}
      {error && <ApiErrorMessageWithTrans error={error} />}
    </div>
  );
};
